import React, { useState, useMemo } from 'react';
import { AlarmClock } from 'lucide-react';
import type { Alarm } from '@/types/clock';

interface AlarmIndicatorProps {
  alarms: Alarm[];
  onClickNavigate: () => void;
  accentColor: string;
}

const formatAlarmTime = (time: { hour: number; minute: number }): string => {
  const period = time.hour >= 12 ? 'PM' : 'AM';
  const hour = time.hour % 12 || 12;
  const minute = time.minute.toString().padStart(2, '0');
  return `${hour}:${minute} ${period}`;
};

const getTimeUntil = (nextTriggerTs: number): string => {
  const now = Date.now();
  const diff = nextTriggerTs - now;
  
  if (diff < 0) return 'Now';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `in ${days}d`;
  }
  if (hours > 0) {
    return `in ${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `in ${minutes}m`;
  }
  return 'in < 1m';
};

export const AlarmIndicator: React.FC<AlarmIndicatorProps> = ({
  alarms,
  onClickNavigate,
  accentColor
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get enabled alarms sorted by next trigger
  const enabledAlarms = useMemo(() => {
    return alarms
      .filter(a => a.enabled)
      .sort((a, b) => a.nextTriggerTs - b.nextTriggerTs);
  }, [alarms]);

  const nextAlarm = enabledAlarms[0];

  if (enabledAlarms.length === 0) {
    return null;
  }

  return (
    <div className="absolute bottom-4 left-4 z-30">
      <button
        onClick={onClickNavigate}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative group"
        title="View alarms"
      >
        {/* Large prominent badge */}
        <div
          className="px-4 py-3 rounded-2xl flex items-center gap-3 backdrop-blur-xl border-2 transition-all duration-300 hover:scale-105 relative shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${accentColor}25 0%, ${accentColor}15 100%)`,
            borderColor: `${accentColor}50`,
            boxShadow: `0 8px 24px ${accentColor}30, 0 0 60px ${accentColor}15`
          }}
        >
          {/* Alarm clock icon */}
          <div className="relative">
            <AlarmClock 
              className="w-6 h-6 animate-pulse" 
              style={{ color: accentColor }}
            />
            
            {/* Pulse ring around icon */}
            <div
              className="absolute inset-0 rounded-full animate-ping opacity-30"
              style={{
                border: `2px solid ${accentColor}`,
                animationDuration: '2s'
              }}
            />
          </div>
          
          {/* Text content */}
          <div className="flex flex-col">
            <div className="text-white font-bold text-sm leading-tight">
              {enabledAlarms.length} Alarm{enabledAlarms.length > 1 ? 's' : ''}
            </div>
            <div 
              className="text-xs font-medium leading-tight"
              style={{ color: accentColor }}
            >
              {getTimeUntil(nextAlarm.nextTriggerTs)}
            </div>
          </div>
          
          {/* Animated glow effect */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: `radial-gradient(circle at center, ${accentColor}15 0%, transparent 70%)`,
              animation: 'pulse 3s ease-in-out infinite'
            }}
          />
        </div>

        {/* Expanded tooltip on hover */}
        {isHovered && nextAlarm && (
          <div
            className="absolute bottom-full left-0 mb-3 px-5 py-4 rounded-2xl backdrop-blur-xl border-2 shadow-2xl min-w-[240px] animate-in fade-in slide-in-from-bottom-2 duration-200"
            style={{
              background: 'rgba(0, 0, 0, 0.90)',
              borderColor: `${accentColor}40`,
              boxShadow: `0 12px 40px ${accentColor}25, 0 0 80px ${accentColor}15`
            }}
          >
            {/* Arrow */}
            <div
              className="absolute -bottom-2 left-6 w-4 h-4 rotate-45"
              style={{
                background: 'rgba(0, 0, 0, 0.90)',
                borderRight: `2px solid ${accentColor}40`,
                borderBottom: `2px solid ${accentColor}40`
              }}
            />

            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <AlarmClock className="w-4 h-4" style={{ color: accentColor }} />
                <div className="text-xs text-white/60 uppercase tracking-wider font-semibold">Next Alarm</div>
              </div>
              
              <div className="text-2xl font-bold text-white mb-2">
                {formatAlarmTime(nextAlarm.time)}
              </div>
              
              {nextAlarm.label && (
                <div className="text-sm text-white/90 mb-3 font-medium">{nextAlarm.label}</div>
              )}
              
              <div 
                className="inline-block px-3 py-1.5 rounded-lg text-sm font-bold"
                style={{
                  background: `${accentColor}20`,
                  color: accentColor,
                  border: `1px solid ${accentColor}40`
                }}
              >
                {getTimeUntil(nextAlarm.nextTriggerTs)}
              </div>
              
              {enabledAlarms.length > 1 && (
                <div className="mt-4 pt-3 border-t border-white/20">
                  <div className="text-xs text-white/70 font-medium">
                    <span style={{ color: accentColor }} className="font-bold">+{enabledAlarms.length - 1}</span> more alarm{enabledAlarms.length > 2 ? 's' : ''} scheduled
                  </div>
                </div>
              )}
              
              <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/50 text-center">
                Click to manage alarms
              </div>
            </div>
          </div>
        )}
      </button>
    </div>
  );
};
